import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import classNames from "classnames";

import RichTextBlock from "./RichTextBlock";
import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";
import TCPACopy from "./TCPACopy";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import {
    getFormattedPhoneNumber,
    isValidEmail,
    isValidPhoneNumber,
    isValidName,
} from "../utils/utils";

import "../styles/components/RequestDemoForm.scss";

const TIME_INTERVALS = [
    { value: "9am - 12pm", label: "9am - 12pm" },
    { value: "12pm - 3pm", label: "12pm - 3pm" },
    { value: "3pm - 6pm", label: "3pm - 6pm" },
    { value: "6pm - 9pm", label: "6pm - 9pm" },
];

const GuardedDownloadForm = ({ lead, successMessage }) => {
    const [facilityName, setFacilityName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [time, setTime] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updateEmail = e => {
        setEmail(e.target.value);
        setValidity("email", isValidEmail(e.target.value));
    };

    const updatePhoneNumber = e => {
        let number = getFormattedPhoneNumber(e.target.value);
        setPhoneNumber(number);
        setValidity("phoneNumber", isValidPhoneNumber(number));
    };

    const updateZipCode = e => {
        let code = e.target.value;
        code = code.replace(/[^0-9]/g, "");
        setZipCode(code);
        if (code.length === 5) {
            e.currentTarget.blur();
        }
        setValidity("zipCode", code.length === 5);
    };

    const isValid = () => {
        const _errors = {
            facilityName: !isValidName(facilityName),
            firstName: !isValidName(firstName),
            lastName: !isValidName(lastName),
            email: !isValidEmail(email),
            phoneNumber: phoneNumber && !isValidPhoneNumber(phoneNumber),
            zipCode: zipCode.length && zipCode.length !== 5,
            time: !time,
        };
        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { demo: endpointUrl },
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            facility_name: facilityName,
            first_name: firstName,
            last_name: lastName,
            zip_code: zipCode,
            schedule: time,
            phone: phoneNumber,
            recaptcha_token: reCAPTCHAToken,
            email,
            referer: window?.location?.href || "",
        };
        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        const token = await reCAPTCHARef.current.executeAsync();
        submitForm(token);
    };

    if (submitted && !submissionError) {
        return (
            <div className="alert-container">
                <Alert variant="success" className="pl-4">
                    {successMessage ? (
                        <RichTextBlock content={successMessage} />
                    ) : (
                        "Thank you! Your download is ready."
                    )}
                </Alert>
            </div>
        );
    }

    return [
        <Form
            className={classNames("request-demo-form", {
                "has-submit-attempt": hasSubmitAttempt,
            })}
            onSubmit={executeReCAPTCHA}
            key="guarded-download-form"
        >
            {lead && (
                <div className="form-lead">
                    <RichTextBlock content={lead} />
                </div>
            )}
            <Form.Group
                className="form-submit-container align-items-center"
                as={Row}
            >
                <Col xs={12} className="mb-2">
                    <span className="required-label">
                        <sup>*</sup>Required
                    </span>
                </Col>
                <Col xs={12} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        required
                        value={facilityName}
                        onChange={e => setFacilityName(e.target.value)}
                        placeholder="*Organization"
                        className={classNames({
                            invalid: !!errors["facilityName"],
                        })}
                    />
                </Col>

                <Col md={6} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        required
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder="*First Name"
                        className={classNames({
                            invalid: !!errors["firstName"],
                        })}
                    />
                </Col>
                <Col md={6} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        required
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder="*Last Name"
                        className={classNames({
                            invalid: !!errors["lastName"],
                        })}
                    />
                </Col>

                <Col xs={12} sm={9} className="mb-4">
                    <Form.Control
                        type="email"
                        size="lg"
                        required
                        value={email}
                        onChange={updateEmail}
                        placeholder="*Work Email"
                        className={classNames({
                            invalid: !!errors["email"],
                        })}
                    />
                </Col>
                <Col sm={3} className="mb-4">
                    <Form.Control
                        type="text"
                        size="lg"
                        value={zipCode}
                        onChange={updateZipCode}
                        placeholder="*Zip code"
                        className={classNames({
                            invalid: !!errors["zipCode"],
                        })}
                    />
                </Col>

                <Col md={6} className="mb-4">
                    <Form.Control
                        type="tel"
                        maxLength="14"
                        size="lg"
                        placeholder="*Enter your phone"
                        onChange={updatePhoneNumber}
                        value={phoneNumber}
                        className={classNames({
                            invalid: !!errors["phoneNumber"],
                        })}
                    />
                </Col>
                <Col md={6} className="mb-4">
                    <Form.Control
                        as={Form.Select}
                        size="lg"
                        placeholder="*Best Time to Schedule"
                        defaultValue={time}
                        onChange={e => setTime(e.target.value)}
                        className={classNames({
                            "select-placeholder": !time,
                            invalid: !!errors["time"],
                        })}
                    >
                        <option value="" disabled>
                            Best Time to Schedule*
                        </option>
                        {TIME_INTERVALS.map(interval => (
                            <option value={interval.value} key={interval.value}>
                                {interval.label}
                            </option>
                        ))}
                    </Form.Control>
                </Col>
                <Col xs={12} className="mb-2">
                    <Button
                        variant="primary"
                        type="submit"
                        size="lg"
                        disabled={submitting}
                        className="px-5"
                    >
                        Download Now
                    </Button>
                </Col>
            </Form.Group>
            {submissionError && (
                <Alert variant="danger">{submissionError}</Alert>
            )}
            <Form.Text className="compliance">
                <TCPACopy />
            </Form.Text>
            <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
        </Form>,
    ];
};

GuardedDownloadForm.propTypes = {
    lead: PropTypes.shape({ raw: PropTypes.string }),
    successMessage: PropTypes.shape({ raw: PropTypes.string }),
};

export default GuardedDownloadForm;
